import { useState } from "react";
import Sidebar from "./Sidebar";
import SidebarV2 from "./SidebarV2";
import AnnouncementBanner from "../AnnouncementBanner";
import SecurityAssurance from "../SecurityAssurance";
import { useAuth } from "../../context/AuthContext";
import { Crown, Menu } from "lucide-react";
import { ThemeToggle } from "../ui/ThemeToggle";
import ChangePasswordDialog from "../ChangePasswordDialog";
import { isNavV2Enabled } from "../../lib/featureFlags";

const DashboardLayout = ({ children, title, subtitle }) => {
  const { user } = useAuth();
  const isPioneer = !!(user?.is_pioneer && user?.pioneer_number);
  // nav_v2 flag (default OFF) → redesigned sidebar; otherwise the shipped one.
  // Reads the DB-backed flag off the user (system_settings.feature_flags), with
  // a localStorage QA override. See lib/featureFlags.js (nav-ia-008).
  const navV2 = isNavV2Enabled(user);
  // Mobile drawer state — only meaningful for SidebarV2 (nav-ia-011).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  return (
    <div className="min-h-screen bg-obsidian dashboard-shell">
      {navV2
        ? <SidebarV2 mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
        : <Sidebar />}
      {/* SidebarV2 is an off-canvas drawer below lg, so its rail doesn't reserve
          space there; the shipped sidebar keeps the fixed ml-64 offset. */}
      <main className={`${navV2 ? "ml-0 lg:ml-64" : "ml-64"} min-h-screen`}>
        {/* Header — frosted bar driven by the theme tokens (light-theme-002b):
            navy in dark, near-white in light. The title/subtitle use cream/
            platinum, which are bridged (→ navy in light), so they stay readable
            on both. */}
        <header className="sticky top-0 z-40 px-8 py-6 border-b border-platinum/10 bg-[var(--surface-frosted)] backdrop-blur-md">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              {navV2 && (
                <button
                  type="button"
                  onClick={() => setMobileNavOpen(true)}
                  aria-label="Open navigation menu"
                  data-testid="mobile-nav-open"
                  className="lg:hidden shrink-0 -ml-1 mt-0.5 p-2 rounded-lg text-platinum/80 hover:text-cream hover:bg-white/5 transition-colors"
                >
                  <Menu size={22} />
                </button>
              )}
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-cream font-serif">{title}</h1>
                {subtitle && <p className="text-platinum/65 text-sm mt-1">{subtitle}</p>}
              </div>
            </div>
            <div className="shrink-0 flex items-center gap-3">
              {/* Authenticated self-service password change (forge-20260615-001).
                  Lives in the shared member chrome so every dashboard (client,
                  referrer, etc.) gets it without a dedicated settings page. */}
              <ChangePasswordDialog
                triggerVariant="outline"
                triggerSize="sm"
                triggerClassName="border-platinum/30 text-platinum hover:text-cream"
              />
              {isPioneer && (
                <a
                  href="/pioneers"
                  data-testid="header-pioneer-badge"
                  title={`You are Founding Pioneer №${String(user.pioneer_number).padStart(3, "0")}. Click to visit the Wall of Champions.`}
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tabular-nums uppercase tracking-wider whitespace-nowrap border border-amber-400/50 bg-gradient-to-r from-amber-400/20 via-amber-500/10 to-amber-400/5 text-amber-300 shadow-[0_0_20px_rgba(234,179,8,0.30)] hover:shadow-[0_0_28px_rgba(234,179,8,0.55)] hover:border-amber-300/80 transition-all"
                >
                  <Crown size={12} className="text-amber-300" />
                  Pioneer №{String(user.pioneer_number).padStart(3, "0")}
                </a>
              )}
              <ThemeToggle size="sm" />
            </div>
          </div>
        </header>

        {/* Content — pb-24 ensures the floating HelpPopover bubble (fixed bottom-6 right-6, 56×56) never overlaps page CTAs */}
        <div className="p-8 pb-24">
          <AnnouncementBanner />
          {children}

          {/* Portal-wide cybersecurity reassurance — sits at the bottom of every dashboard page */}
          <div className="mt-12 pt-6 border-t border-platinum/10">
            <SecurityAssurance variant="footer" className="text-center" />
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
